import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";

import { StoreState } from "../../redux/configure-store";
import PageTitle from "../components/PageTitle";
import ListHeader from "../../common/components/list-header";
import { formatShortDate } from "../../common/utils";
import { toggleHideUnavailableUsersForAdmin } from "../admin-actions";
import { User } from "../types";
import { backgrounds, borders } from "../../theme/theme";

function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
}

function UsersTable(props: any) {
  const {
    isHideUnavailableUsers,
    navigateToAdminUsersViewPage,
    users,
  } = props;

  const [searchQuery, setSearchQuery] = useState("");
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);

  const filterUsers = useCallback(() => {
    let usersToDisplay = isHideUnavailableUsers
      ? users.filter((u: any) => u.numberOfHoursPerWeekAvailable !== 0)
      : users;
    if (!searchQuery) {
      return usersToDisplay;
    }

    const searchTerms = searchQuery.split(" ");

    return usersToDisplay.filter((u: User) => {
      let numberOfMatches = 0;
      searchTerms.forEach((searchTerm) => {
        if (
          (u.technicalDescription &&
            u.technicalDescription.search(
              new RegExp(escapeRegExp(searchTerm), "i")
            ) > -1) ||
          (u.shortTechnicalLabel &&
            u.shortTechnicalLabel.search(
              new RegExp(escapeRegExp(searchTerm), "i")
            ) > -1)
        ) {
          numberOfMatches++;
        }
      });
      return numberOfMatches === searchTerms.length;
    });
  }, [users, isHideUnavailableUsers, searchQuery])

  useEffect(() => {
    setFilteredUsers(filterUsers());
  }, [filterUsers]);

  function handleSubmit() {
    setFilteredUsers(() => filterUsers());
  }

  function keyPressed(event: any) {
    if (event.key === "Enter") {
      handleSubmit();
    }
  }

  function handleSearchQueryChange(searchQuery: string) {
    setSearchQuery(searchQuery);
  }

  const columnNames = [
    "User Id",
    "Full Name",
    "Technical Description",
    "Login/ Email Address",
    "Country",
    "Hours Available per Week",
    "Hourly Rate In USD",
    "Short Technical Label (frontend, backend, ...)",
    "Admin",
    "Last Active",
  ];

  return (
    <div>
      <PageTitle>Users</PageTitle>
      <Box sx={{ mb: 3, p: 2, backgroundColor: backgrounds.page, borderRadius: 1, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <TextField
          placeholder="Search by languages, frameworks..."
          size="small"
          onKeyPress={keyPressed}
          onChange={(e) => handleSearchQueryChange(e.target.value)}
          sx={{ minWidth: "300px", backgroundColor: "white" }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleSubmit()}
        >
          Search
        </Button>
        <Button
          onClick={() => props.toggleHideUnavailableUsersForAdmin()}
          variant="outlined"
          color="primary"
        >
          {props.isHideUnavailableUsers ? "Show" : "Hide"} Unavailable Users
        </Button>
      </Box>
      <Box sx={{ mt: 1 }}>
        <Paper elevation={0} sx={{ width: "100%", overflowX: "auto", border: `1px solid ${borders.light}`, borderRadius: 2 }}>
          <Table sx={{ minWidth: 700, mb: 5 }}>
            <ListHeader columns={columnNames} />
            <TableBody>
              {((filteredUsers || []) as User[]).map((user) => {
                return (
                  <TableRow
                    sx={{ cursor: "pointer" }}
                    key={user.id}
                    onClick={() => navigateToAdminUsersViewPage(user.id)}
                  >
                    <TableCell component="th" scope="row">
                      {user.id}
                    </TableCell>
                    <TableCell>
                      {user.firstName} {user.lastName}
                    </TableCell>
                    <TableCell>{user.technicalDescription}</TableCell>
                    <TableCell>{user.emailAddress}</TableCell>
                    <TableCell>{user.country}</TableCell>
                    <TableCell>{user.numberOfHoursPerWeekAvailable}</TableCell>
                    <TableCell>{user.hourlyRateInUsd}</TableCell>
                    <TableCell>{user.shortTechnicalLabel}</TableCell>
                    <TableCell>{user.isAdmin ? "true" : "false"}</TableCell>
                    <TableCell>{formatShortDate(user.lastActiveAt)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </div>
  );
}

const mapStateToProps = (storeState: StoreState) => ({
  isHideUnavailableUsers: storeState.admin.isHideUnavailableUsers,
});

export default connect(mapStateToProps, { toggleHideUnavailableUsersForAdmin })(
  UsersTable
);
